import { useCallback, useEffect, useState } from "react";
import restApi from "src/rest";
import {
  clearAllCookies,
  deleteFromLocalStorage,
  getCsrfToken,
  saveToLocalStorage,
} from "src/store";
import { useChat } from "./useChat";
import { useHeap } from "./useHeap";
import { ApiUser, ChatConfig, User } from "src/types";

const USER_KEY = "user";

export const useAuthState = () => {
  const localUserString = localStorage.getItem(USER_KEY);
  const [user, setUser] = useState<User | null>(
    localUserString ? JSON.parse(localUserString) : null
  );
  const [isAuth, setIsAuth] = useState<boolean>(!!getCsrfToken() && !!user);
  const [chatConfig, setChatConfig] = useState<ChatConfig | null>(null);

  const { openChat } = useChat(isAuth, user, chatConfig);
  useHeap(isAuth, user);

  const userAdapter = (userData: ApiUser): User => {
    return {
      id: userData.id,
      firstName: userData.first_name,
      lastName: userData.last_name,
      phone: userData.phone,
      userName: userData.user_name,
      email: userData.email,
    };
  };

  const fetchConfig = useCallback(async () => {
    const config = await restApi.get("configs/");
    setChatConfig(config || null);
  }, [setChatConfig]);

  const fetchUserInformation = useCallback(async () => {
    const response = await restApi.get("users/");
    setUser(userAdapter(response.user));
  }, [setUser]);

  const login = useCallback(
    async (username: string, password: string) => {
      try {
        const response = await restApi.post("login/", {
          username: username,
          password,
        });

        if (response && response.detail) {
          setIsAuth(true);
        }
      } catch (error) {
        throw error;
      }
    },
    [setIsAuth]
  );

  const logout = useCallback(async () => {
    try {
      if (!!getCsrfToken()) {
        await restApi.post("logout/");
        deleteFromLocalStorage(USER_KEY);
        clearAllCookies();
        // set chat config to default values at last...
        setUser(null);
      }
      setIsAuth(false);
    } catch (error) {
      console.error(error);
    }
  }, [setIsAuth]);

  useEffect(() => {
    if (!!getCsrfToken() && !!user) {
      setIsAuth(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      saveToLocalStorage(USER_KEY, JSON.stringify(user));
    } else {
      deleteFromLocalStorage(USER_KEY);
    }
  }, [user]);

  useEffect(() => {
    if (isAuth && !chatConfig) {
      fetchConfig();
    }
    if (isAuth && !user) {
      fetchUserInformation();
    }
  }, [user, isAuth, chatConfig, fetchUserInformation, fetchConfig]);

  return { isAuth, user, login, logout, setUser, openChat };
};
